import React from 'react';
import { useMutation } from 'react-query';
import { Trans } from 'react-i18next';
import { useRequest } from '@datahub/api-client';
import { ApiOperation } from '../../../../../api';
import useGeneralErrorHandler from '../../../../error-handlers/useGeneralErrorHandler';
import useOnError from '../../../../error-handlers/useOnError';
import { queryClient } from '../../../../../queryClient';
import {
  getInProgressInvoicesQueryKey,
  getProcessedInvoicesQueryKey,
  getToApproveInvoicesQueryKey,
  getToVerifyInvoicesQueryKey,
} from '../../../../queries/queryKeys';
import { rejectToAmendInvoiceMutationKey } from '../../../mutationKeys';
import { InvoiceMutationPropsInterface } from '../InvoiceMutationProps.interface';
import { useAlert } from '../../../../../alert/Alert.provider';
import { InvoiceRejectPayloadInterface } from './interfaces/InvoiceRejectPayload.interface';

const useRejectToAmendInvoiceMutation = ({ setOtherError }: InvoiceMutationPropsInterface) => {
  const { showAlert } = useAlert();

  const { request } = useRequest(ApiOperation.RejectToAmendInvoice);

  // TODO: handle other errors if needed
  const generalErrorHandler = useGeneralErrorHandler(setOtherError);

  const { onError } = useOnError([], generalErrorHandler);

  return useMutation(
    async (payload: InvoiceRejectPayloadInterface) => {
      const { invoiceId: id } = payload;
      const res = await request({ data: { ...payload }, urlParams: { id } });
      return res.data;
    },
    {
      mutationKey: rejectToAmendInvoiceMutationKey,
      onSuccess: (_, { invoiceReference: name }: InvoiceRejectPayloadInterface) => {
        queryClient.invalidateQueries(getInProgressInvoicesQueryKey);
        queryClient.invalidateQueries(getToVerifyInvoicesQueryKey);
        queryClient.invalidateQueries(getToApproveInvoicesQueryKey);
        queryClient.invalidateQueries(getProcessedInvoicesQueryKey);

        showAlert(
          <Trans
            i18nKey="invoices.tabs.toApprove.actions.rejectToAmend.success"
            values={{ name }}
            components={{ bold: <strong /> }}
          />,
          'info',
        );
      },
      onError,
    },
  );
};

export default useRejectToAmendInvoiceMutation;
